import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Send, Ban, Download, BarChart3, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StockViewModal from './StockViewModal';
import { ReceiptHistory } from './ReceiptHistory';
import { fmtQ } from '@/components/po/PoLinePicker';
import { LineStatusCell } from '@/components/po/LineStatusCell';

const UnitTotals = ({ po, k, className = '', testId }) => (
  <div className={`text-xl font-bold ${className}`} data-testid={testId}>
    {(po.totals_by_unit || []).length === 0 ? '0' : po.totals_by_unit.map((u) => (
      <div key={u.unit} className="leading-tight">{fmtQ(u[k])} <span className="text-xs font-medium text-gray-500">{u.unit}</span></div>))}
  </div>
);

const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-300',
  ISSUED: 'bg-blue-100 text-blue-700 border-blue-300',
  PARTIAL_RECEIVED: 'bg-amber-100 text-amber-800 border-amber-300',
  COMPLETED: 'bg-green-100 text-green-700 border-green-300',
  CANCELLED: 'bg-red-100 text-red-700 border-red-300',
};

const PODetail = () => {
  const navigate = useNavigate();
  const { poId } = useParams();
  const [po, setPo] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pct, setPct] = useState(5);
  const [issuing, setIssuing] = useState(false);
  const [stockItemId, setStockItemId] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/purchase-orders/${poId}`);
      setPo(r.data);
      const rr = await apiClient.get(`/purchase-orders/${poId}/receipts`);
      setReceipts(rr.data.receipts || []);
      const st = await apiClient.get('/po-receipts/settings');
      setPct(st.data.over_receipt_pct);
    } catch (e) {
      toast.error('Failed to load PO');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [poId]);

  const handleIssue = async () => {
    if (!window.confirm('Issue this PO? A PDF will be generated and status will become ISSUED.')) return;
    setIssuing(true);
    try {
      await apiClient.post(`/purchase-orders/${poId}/issue`);
      toast.success('PO issued — PDF generated');
      await fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to issue');
    } finally { setIssuing(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this PO? This cannot be undone.')) return;
    try {
      await apiClient.post(`/purchase-orders/${poId}/cancel`);
      toast.success('PO cancelled');
      await fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const r = await apiClient.get(`/purchase-orders/${poId}/pdf`, { responseType: 'blob' });
      // Safety: some proxies swap content-type when an error HTML page is returned.
      // Only refuse the blob when we can confirm it's NOT a PDF.
      const blobType = r.data?.type || '';
      const headerCT = r.headers?.['content-type'] || '';
      const looksLikePdf = blobType.includes('pdf') || headerCT.includes('pdf') || r.data?.size > 100;
      if (!looksLikePdf) {
        const text = await r.data.text().catch(() => '');
        let detail = text && text.slice(0, 200);
        try { detail = JSON.parse(text).detail || detail; } catch (_) { /* keep text */ }
        toast.error(`PDF unavailable: ${detail || 'empty response'}`);
        return;
      }
      // Force a PDF type on the blob so browsers render it inline instead of downloading raw bytes
      const pdfBlob = r.data.type ? r.data : new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      const fileName = `${(po?.po_number || poId).replace(/\//g, '_')}.pdf`;

      // Always use the anchor-download approach — reliable on iOS Safari, Android Chrome, and desktop.
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success('PDF opened');
    } catch (e) {
      // Try to extract backend error from a blob response
      const status = e.response?.status;
      let detail = e.response?.data?.detail;
      if (!detail && e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          detail = JSON.parse(txt).detail;
        } catch (_) { /* ignore */ }
      }
      const msg = detail || e.message || 'Could not open PDF';
      toast.error(status ? `PDF failed (${status}): ${msg}` : `PDF failed: ${msg}`);
      console.error('[PO PDF] download error', { status, message: e.message, response: e.response });
    }
  };

  if (loading || !po) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  const canEdit = po.status === 'DRAFT';
  const canIssue = po.status === 'DRAFT' && (po.items || []).length > 0;
  const canReceive = ['ISSUED', 'PARTIAL_RECEIVED'].includes(po.status);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="po-detail-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/po')}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold flex items-center gap-2">{po.po_number}<Badge className={STATUS_COLORS[po.status]} variant="outline">{po.status}</Badge></h1>
          <p className="text-xs text-gray-500">{po.vendor_name} · {po.section_name}</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleDownloadPdf} data-testid="download-pdf-btn"><Download className="w-4 h-4 mr-1" />PDF</Button>
        {canEdit && <Button variant="outline" size="sm" onClick={() => navigate(`/po/create?edit=${poId}`)}><FileText className="w-4 h-4 mr-1" />Edit</Button>}
        {canIssue && <Button onClick={handleIssue} disabled={issuing} data-testid="issue-po-btn"><Send className="w-4 h-4 mr-1" />{issuing ? 'Issuing...' : 'Issue PO'}</Button>}
        {po.status !== 'COMPLETED' && po.status !== 'CANCELLED' && (
          <Button variant="outline" size="sm" onClick={handleCancel} className="text-red-600" data-testid="cancel-po-btn"><Ban className="w-4 h-4 mr-1" />Cancel</Button>
        )}
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Ordered Qty</p><UnitTotals po={po} k="ordered" testId="po-total-ordered" /></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Received Qty</p><UnitTotals po={po} k="received" className="text-green-700" testId="po-total-received" /></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Balance Qty</p><UnitTotals po={po} k="balance" className="text-amber-700" testId="po-total-balance" /></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Grand Total</p><p className="text-xl font-bold text-indigo-700">₹ {po.grand_total?.toLocaleString('en-IN')}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Issued At</p><p className="text-sm font-semibold">{po.issued_to_supplier_at ? new Date(po.issued_to_supplier_at).toLocaleString() : '—'}</p></CardContent></Card>
        </div>

        {/* Items */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Items</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier Design</TableHead>
                    <TableHead>Our Sort No</TableHead>
                    <TableHead>Photos</TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(po.items || []).map(it => (
                    <TableRow key={it.po_item_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-sm">{it.supplier_design_name}</div>
                            <div className="text-xs text-gray-400">{it.remarks}</div>
                            {it.pr_id && <Link to={`/pr/${it.pr_id}`} className="text-[11px] font-mono text-indigo-600 hover:underline" data-testid={`po-item-pr-link-${it.po_item_id}`}>{it.pr_number}</Link>}
                          </div>
                          {it.mapped_fg_name && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-indigo-600 hover:text-indigo-800"
                              onClick={() => setStockItemId(it.po_item_id)}
                              title={`View stock for ${it.mapped_fg_name}`}
                              data-testid={`view-stock-${it.po_item_id}`}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{it.mapped_sort_no || <span className="text-gray-400 italic">— none —</span>}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(it.image_urls || []).slice(0, 3).map(u => <img key={u} src={u} className="w-10 h-10 object-cover rounded border" alt="" />)}
                          {(it.image_urls || []).length > 3 && <span className="text-xs text-gray-500 self-center">+{it.image_urls.length - 3}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm">{it.ordered_qty} {it.unit}<div className="text-[10px] text-gray-400">limit {fmtQ(it.ordered_qty * (1 + pct / 100))}</div></TableCell>
                      <TableCell className="text-right text-sm text-green-700" data-testid={`po-item-received-${it.po_item_id}`}>{fmtQ(it.received_qty)}
                        {it.excess_qty > 0 && <div className="text-[10px] text-amber-700">+{fmtQ(it.excess_qty)} excess</div>}</TableCell>
                      <TableCell className="text-right text-sm font-semibold" data-testid={`po-item-balance-${it.po_item_id}`}>{fmtQ(it.balance_qty)}</TableCell>
                      <TableCell><LineStatusCell item={it} poOpen={canReceive} poLive={canReceive || po.status === 'COMPLETED'} onChanged={fetch} /></TableCell>
                      <TableCell className="text-right text-sm">₹ {it.rate} + {it.tax_pct}%</TableCell>
                      <TableCell className="text-right text-sm">₹ {it.amount?.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {canReceive && (
              <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-500" data-testid="po-receive-note">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Quantities are counted in the sections — Greige Sort No Assignment, FOB Sort No → USN Tagging, or the section's Purchase Receipt screen — up to {pct}% above the ordered qty.
              </p>
            )}
          </CardContent>
        </Card>

        <ReceiptHistory receipts={receipts} items={po.items || []} onChanged={fetch} />
      </div>

      <StockViewModal
        open={!!stockItemId}
        onClose={() => setStockItemId(null)}
        poItemId={stockItemId}
      />
    </div>
  );
};

export default PODetail;
